/**
 * ============================================================================
 * KpiTilesGrid — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: 8 KPI tiles in a grid, each with a 12-point sparkline drawn as
 *          inline SVG (no chart library). Also renders the supplier's
 *          aggregate quality score as a gauge above the grid.
 *
 * Sparkline: hand-rolled — see TrendSparkline in QualityMetricsDashboard for
 *  the canonical implementation. The visual mirrors that one (mint→cyan→blue
 *  gradient stroke, soft dot at each point).
 * ============================================================================ */

import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SupplierDetail.module.css';
import type {
  SupplierKpi,
  SupplierKpiKey,
  SupplierKpiTrendPoint,
} from './useSupplierDetailQuery';

const BAND_DOT_CLASS = {
  GREEN: styles.dotNeutral,
  AMBER: styles.dotWarn,
  RED: styles.dotCritical,
  UNKNOWN: styles.dotMuted,
} as const;

const KPI_ORDER: SupplierKpiKey[] = [
  'OTIF',
  'DELIVERY_PERFORMANCE',
  'DEVIATION_COUNT',
  'COMPLAINT_COUNT',
  'OOS_RATE',
  'AUDIT_SCORE',
  'CAPA_EFFECTIVENESS',
  'QUALITY_INCIDENTS',
];

function formatValue(k: SupplierKpi, locale: string): string {
  if (k.value == null) return '—';
  const nf = new Intl.NumberFormat(locale, {
    maximumFractionDigits: k.unit_kind === 'PERCENTAGE' ? 1 : 0,
  });
  switch (k.unit_kind) {
    case 'PERCENTAGE': return `${nf.format(k.value)}%`;
    case 'DAYS': return `${nf.format(k.value)} d`;
    case 'SCORE': return nf.format(k.value);
    default: return nf.format(k.value);
  }
}

export interface KpiTilesGridProps {
  kpis: SupplierKpi[];
  aggregateQualityScore: number | null;
}

export function KpiTilesGrid({ kpis, aggregateQualityScore }: KpiTilesGridProps) {
  const { t, i18n } = useTranslation();
  const byKey = useMemo(() => {
    const m = new Map<SupplierKpiKey, SupplierKpi>();
    kpis.forEach((k) => m.set(k.key, k));
    return m;
  }, [kpis]);

  return (
    <div className={styles.kpiGridWrap}>
      <AggregateQualityGauge value={aggregateQualityScore} />

      <div
        className={styles.kpiGrid}
        role="list"
        aria-label={t('supplier.detail.kpis.gridAria', {
          defaultValue: 'Supplier KPI tiles',
        })}
      >
        {KPI_ORDER.map((key) => {
          const k = byKey.get(key);
          const label = t(`supplier.detail.kpi.${key}.label`, {
            defaultValue: k?.label ?? key,
          });
          if (!k) {
            return (
              <div key={key} role="listitem" className={styles.kpiTileEmpty}>
                <header className={styles.kpiTileHeader}>
                  <span className={styles.kpiTileLabel}>{label}</span>
                </header>
                <div className={styles.kpiTileValue}>—</div>
              </div>
            );
          }
          const valueStr = formatValue(k, i18n.language);
          const bandLabel = t(`supplier.detail.band.${k.band}`, {
            defaultValue: k.band,
          });
          return (
            <article
              key={key}
              role="listitem"
              className={styles.kpiTile}
              aria-label={t('supplier.detail.kpis.tileAria', {
                defaultValue: '{{label}} — {{value}} ({{band}})',
                label,
                value: valueStr,
                band: bandLabel,
              })}
            >
              <header className={styles.kpiTileHeader}>
                <span className={styles.kpiTileLabel}>{label}</span>
                <span
                  className={`${styles.dot} ${BAND_DOT_CLASS[k.band]}`}
                  aria-hidden="true"
                />
              </header>
              <div className={styles.kpiTileValue}>{valueStr}</div>
              <KpiSparkline points={k.trend} />
              <div className={styles.kpiTileBand}>{bandLabel}</div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * KpiSparkline — inline SVG. Same look as TrendSparkline in QMD.
 * --------------------------------------------------------------------------- */

const SW = 140;
const SH = 36;
const SPAD = 2;

function KpiSparkline({ points }: { points: SupplierKpiTrendPoint[] }) {
  const { t } = useTranslation();
  const gradId = useId();

  if (points.length === 0) {
    return (
      <div
        className={styles.sparklineEmpty}
        role="img"
        aria-label={t('supplier.detail.kpis.trendEmpty', {
          defaultValue: 'No trend data yet',
        })}
      >
        —
      </div>
    );
  }

  const values = points.map((p) => p.value);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const range = hi - lo === 0 ? 1 : hi - lo;
  const step = (SW - SPAD * 2) / Math.max(points.length - 1, 1);
  const coords = values.map((v, i) => ({
    x: SPAD + i * step,
    y: SH - SPAD - ((v - lo) / range) * (SH - SPAD * 2),
  }));
  const d = coords
    .map((c, i) =>
      i === 0 ? `M${c.x.toFixed(2)},${c.y.toFixed(2)}` : `L${c.x.toFixed(2)},${c.y.toFixed(2)}`,
    )
    .join(' ');
  const first = values[0]!;
  const last = values[values.length - 1]!;
  const direction = last > first ? 'up' : last < first ? 'down' : 'flat';

  return (
    <svg
      className={styles.sparkline}
      viewBox={`0 0 ${SW} ${SH}`}
      width={SW}
      height={SH}
      role="img"
      aria-label={t('supplier.detail.kpis.trendAria', {
        defaultValue: 'Trend over {{n}} months: {{direction}} (range {{min}}–{{max}})',
        n: points.length,
        direction,
        min: lo,
        max: hi,
      })}
      focusable="false"
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#1DD1A1" />
          <stop offset="50%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#2563EB" />
        </linearGradient>
      </defs>
      <path
        d={d}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={1.5} fill={`url(#${gradId})`}>
          <title>{`${values[i]}`}</title>
        </circle>
      ))}
    </svg>
  );
}

/* ----------------------------------------------------------------------------
 * AggregateQualityGauge — circular gauge with gradient stroke. Inline SVG.
 * --------------------------------------------------------------------------- */

interface AggregateQualityGaugeProps {
  value: number | null;
}

function AggregateQualityGauge({ value }: AggregateQualityGaugeProps) {
  const { t, i18n } = useTranslation();
  const gradId = useId();
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 });

  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  const r = 56;
  const c = 2 * Math.PI * r;
  const dash = (v / 100) * c;

  return (
    <section
      className={styles.gaugeWrap}
      aria-label={t('supplier.detail.kpis.gaugeAria', {
        defaultValue: 'Aggregate quality score: {{v}} out of 100',
        v: value == null ? '—' : nf.format(v),
      })}
    >
      <svg
        width={140}
        height={140}
        viewBox="0 0 140 140"
        role="img"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1DD1A1" />
            <stop offset="50%" stopColor="#22D3EE" />
            <stop offset="100%" stopColor="#2563EB" />
          </linearGradient>
        </defs>
        <circle
          cx={70}
          cy={70}
          r={r}
          stroke="var(--color-border)"
          strokeWidth={10}
          fill="none"
        />
        <circle
          cx={70}
          cy={70}
          r={r}
          stroke={`url(#${gradId})`}
          strokeWidth={10}
          fill="none"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          transform="rotate(-90 70 70)"
        />
        <text
          x={70}
          y={74}
          textAnchor="middle"
          fontSize={28}
          fontWeight={700}
          fill="var(--color-text)"
        >
          {value == null ? '—' : nf.format(v)}
        </text>
      </svg>
      <div className={styles.gaugeLabel}>
        {t('supplier.detail.kpis.gaugeLabel', {
          defaultValue: 'Aggregate quality score',
        })}
      </div>
    </section>
  );
}
